import { useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import api, { apiErr } from "../../lib/api";
import { toast } from "sonner";
import { cn } from "../../lib/utils";

// Reusable push-to-talk button. Records audio and transcribes it via Whisper,
// then hands the Spanish transcript back through onTranscript.
export function VoiceButton({ onTranscript, className, testId = "voice-button" }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const toggle = async () => {
    if (recording) { recorderRef.current?.stop(); return; }
    if (busy) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false); setBusy(true);
        try {
          const blob = new Blob(chunksRef.current, { type: "audio/webm" });
          const fd = new FormData();
          fd.append("audio", blob, "audio.webm");
          const { data } = await api.post("/audit/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (data.text) onTranscript?.(data.text);
          else toast.error("No se detectó voz. Inténtalo de nuevo.");
        } catch (err) { toast.error(apiErr(err, "No se pudo transcribir el audio.")); }
        finally { setBusy(false); }
      };
      recorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch {
      toast.error("No se pudo acceder al micrófono. Revisa los permisos del navegador.");
    }
  };

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={toggle}
      disabled={busy}
      title="Dictar por voz"
      className={cn(
        "h-11 w-11 shrink-0 rounded-xl flex items-center justify-center transition-colors",
        recording ? "bg-red-500 text-white hover:bg-red-400" : "bg-white/[0.06] text-white hover:bg-white/[0.1]",
        className
      )}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
    </button>
  );
}
